import { memo } from "react";
import { Badge, Button, Col } from "react-bootstrap";
import { MdOutlineEditNote } from "react-icons/md";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { AttendeeNote } from "../../types/Types";
import NoteAvatar from "./AttendeeNoteAvatar";
import {
  CARD_SHADOW,
  DANGER_BTN_STYLE,
  NOTE_CLAMP,
  PRIMARY_BTN_STYLE,
  SECONDARY_BTN_STYLE,
  formatNoteDate,
} from "./attendeeNotesShared";

type AttendeeNoteCardProps = {
  note: AttendeeNote;
  locale: string;
  selfObjectId?: string;
  deletingId: number | null;
  deleting: boolean;
  onEdit: (note: AttendeeNote) => void;
  onView: (note: AttendeeNote) => void;
  onDelete: (id: number) => void;
};

function AttendeeNoteCard({
  note,
  locale,
  selfObjectId,
  deletingId,
  deleting,
  onEdit,
  onView,
  onDelete,
}: AttendeeNoteCardProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const displayDate = formatNoteDate(
    note.updatedAtUtc || note.createdAtUtc,
    locale
  );
  const isPersonal = !note.eventId;
  const isSelfNote =
    !!selfObjectId && note.attendeeObjectId === selfObjectId;
  const fullName = isSelfNote
    ? t("attendeeNotes.you")
    : `${note.attendeeFirstName} ${note.attendeeLastName}`.trim() ||
      t("attendeeNotes.unknownAttendee");

  return (
    <Col xs={12} md={6} xl={4}>
      <div
        className="bg-white rounded-3 h-100 d-flex flex-column p-3"
        style={CARD_SHADOW}
      >
        <div className="d-flex align-items-start gap-3 mb-3">
          <NoteAvatar note={note} />
          <div className="min-w-0 flex-grow-1">
            <div className="fw-bold text-dark text-truncate">{fullName}</div>
            {isPersonal ? (
              <Badge bg="light" text="dark" pill className="fw-normal mt-1">
                {t("attendeeNotes.personalBadge")}
              </Badge>
            ) : note.eventName ? (
              <Badge
                bg="light"
                text="dark"
                pill
                className="fw-normal mt-1 text-truncate mw-100"
                title={note.eventName}
              >
                {note.eventName}
              </Badge>
            ) : null}
            {displayDate ? (
              <div className="text-muted small mt-1">{displayDate}</div>
            ) : null}
          </div>
        </div>

        <div className="d-flex gap-2 align-items-start flex-grow-1 mb-3">
          <MdOutlineEditNote
            size={18}
            className="text-secondary flex-shrink-0 mt-1"
            aria-hidden
          />
          <p className="mb-0 text-dark small flex-grow-1" style={NOTE_CLAMP}>
            {note.noteText}
          </p>
        </div>

        <div className="d-flex gap-2 mt-auto pt-3 border-top">
          <Button
            className="flex-fill rounded-2 border-0 shadow-sm fw-medium text-white text-nowrap"
            style={PRIMARY_BTN_STYLE}
            onClick={() => onEdit(note)}
          >
            {t("attendeeNotes.edit")}
          </Button>
          {isPersonal ? (
            <Button
              className="flex-fill rounded-2 border-0 shadow-sm fw-medium text-dark text-nowrap"
              style={SECONDARY_BTN_STYLE}
              onClick={() => onView(note)}
            >
              {t("attendeeNotes.viewNote")}
            </Button>
          ) : (
            <Button
              className="flex-fill rounded-2 border-0 shadow-sm fw-medium text-dark text-nowrap"
              style={SECONDARY_BTN_STYLE}
              onClick={() =>
                navigate(`/event-booking/attendees/${note.eventId}`)
              }
            >
              {t("attendeeNotes.viewEventAttendees")}
            </Button>
          )}
          <Button
            className="flex-fill rounded-2 shadow-sm fw-medium text-nowrap"
            style={DANGER_BTN_STYLE}
            disabled={deleting && deletingId === note.id}
            onClick={() => onDelete(note.id)}
          >
            {deleting && deletingId === note.id
              ? t("attendeeNotes.deleting")
              : t("attendeeNotes.delete")}
          </Button>
        </div>
      </div>
    </Col>
  );
}

export default memo(AttendeeNoteCard);
